<!--
@component
Analytics

Single source of truth for analytics:

  1. Initialises the upstream Reuters `Analytics` component (gtag.js +
     initial pageview) when the page is on a Reuters domain.
  2. Registers a virtual pageview on every *subsequent* client-side
     navigation via `afterNavigate`, also gated on the Reuters host.

The first `afterNavigate` is skipped because the upstream component
already fires the initial pageview when it mounts — registering again
there would double-count the first view.

This component should be mounted **once**, at the app-group layout
level (see `pages/(app)/+layout.svelte`). Individual `+page.svelte`
files must not render it themselves.
-->

<script lang="ts">
  import {
    Analytics,
    registerPageview,
  } from '@reuters-graphics/graphics-components';
  import { afterNavigate } from '$app/navigation';
  import { browser } from '$app/environment';
  import { isReutersDotcom } from '$utils/env';
  import { page } from '$app/state';
  import pkg from '$pkg';

  const authors = pkg?.reuters?.graphic?.authors ?? [];

  // Track whether `afterNavigate` is the *first* invocation. SvelteKit
  // fires it once during the initial load too — but the upstream
  // `Analytics` component already registers that initial pageview, so we
  // skip the first call to avoid double-counting.
  let isFirstPageNav = true;

  afterNavigate(() => {
    if (isFirstPageNav) {
      isFirstPageNav = false;
      return;
    }
    if (!browser) return;
    // Reuters-host gatekeeper — same check the upstream Analytics
    // component uses below to decide whether to mount at all. See
    // `src/utils/env/index.ts` for the rules and how to extend them.
    if (!isReutersDotcom(page.url)) return;
    try {
      registerPageview();
    } catch (e) {
      console.error(e);
    }
  });
</script>

{#if isReutersDotcom(page.url)}
  <Analytics {authors} />
{/if}
